/**
 * @file index.tsx
 * @fileoverview Renders the JobPosting schema.org.
 */

import React, { useContext, useCallback } from 'react';
import { JsonLd } from 'react-schemaorg';
import { JobPosting } from 'schema-dts';
import {
  getBaseSalary,
  getDirectApply,
  getExpirationDate,
  isRequiredDataAvailable,
} from '~/jobiqo/components/Job/SchemaOrg/utils';
import { isOrganizationProfilePublished } from '~/jobiqo/components/Organization/utils/organizationStatus';
import { PathContext } from '~/jobiqo/context/app';
import { PathContextInterface } from '~/jobiqo/models/app';
import { Job } from '~/jobiqo/types/types';

interface Props {
  /**
   * The job entity.
   */
  job: Job;
}

const JobSchemaOrg = ({ job }: Props) => {
  if (job['isExcluded'] || !isRequiredDataAvailable(job)) {
    return null;
  }

  const formatLocationToPlace = useCallback((loc, lat, lng) => {
    return {
      '@type': 'Place',
      // @ts-ignore
      ...(lat &&
        lng && {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: lat,
            longitude: lng,
          },
        }),
      address: {
        '@type': 'PostalAddress',
        addressLocality: loc.locality || '',
        addressRegion: loc.administrativeArea || '',
        addressCountry: loc.countryCode || '',
        postalCode: loc.zip || '',
        streetAddress:
          loc.addressLine1 || loc.addressLine2
            ? `${loc.addressLine1} ${loc.addressLine2 ?? ''}`
            : '',
      },
    };
  }, []);

  const emptyLocation = {
    locality: '',
    administrativeArea: '',
    countryCode: '',
    zip: '',
    addressLine1: '',
    addressLine2: '',
  };

  const defaultLogo = '';
  const pathContext: PathContextInterface = useContext(PathContext);

  // Handle the array of locations or fallback to an empty array.
  const locations = Array.isArray(job.location) ? job.location : [];

  const hasLocations = locations.length > 0;
  const hasCoordinates = job.latitude && job.longitude;

  const canDisplayPostingData = locations || (job.latitude && job.description);
  const directApply = getDirectApply(job);

  // Create jobLocation schema.
  const createJobLocationSchema = useCallback(() => {
    if (hasLocations) {
      const locationSchemas = locations.map((loc, index) => {
        const useCoordinates = index === 0 && hasCoordinates;
        return formatLocationToPlace(
          loc,
          useCoordinates ? job.latitude : null,
          useCoordinates ? job.longitude : null,
        );
      });

      return locationSchemas.length === 1
        ? locationSchemas[0]
        : locationSchemas;
    } else if (hasCoordinates) {
      return formatLocationToPlace(emptyLocation, job.latitude, job.longitude);
    }

    // Fallback to empty location.
    return formatLocationToPlace(emptyLocation, null, null);
  }, [
    formatLocationToPlace,
    hasLocations,
    hasCoordinates,
    locations,
    job.latitude,
    job.longitude,
    emptyLocation,
  ]);

  return (
    canDisplayPostingData && (
      <JsonLd<JobPosting>
        item={{
          '@context': 'https://schema.org',
          '@type': 'JobPosting',
          title: job.title,
          description: job.description || '',
          datePosted: job.published || '',
          validThrough: getExpirationDate(job),
          employmentType: job.schemaOrgEmployment || 'FULL_TIME',
          ...(directApply && { directApply }),
          hiringOrganization: {
            '@type': 'Organization',
            name: job.organization ? job.organization : '',
            logo:
              job.logo?.url ||
              job.organizationProfile?.logo?.url ||
              defaultLogo,
            ...(isOrganizationProfilePublished(job.organizationProfile) && {
              sameAs: `${pathContext.host}${job.organizationProfile?.url?.path}`, // We want an absolute path,
            }),
          },
          jobLocation: createJobLocationSchema(),
          ...(job.schemaOrgJobLocationType
            ? {
                jobLocationType: job.schemaOrgJobLocationType,
                applicantLocationRequirements: {
                  '@type': 'Country',
                  name:
                    hasLocations && locations[0]
                      ? locations[0].countryCode || ''
                      : '',
                },
              }
            : null),
          baseSalary: getBaseSalary(job),
        }}
      />
    )
  );
};

export default JobSchemaOrg;
